/**
 * API Cache Utility
 * In-memory cache for API responses with automatic expiration
 */

const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
const cache = new Map()

/**
 * Get cached data if available and not expired
 * @param {string} key - Cache key
 * @returns {any|null} Cached data or null if expired/not found
 */
export const getCached = (key) => {
  const cached = cache.get(key)

  if (!cached) {
    return null
  }

  // Check if expired
  if (Date.now() - cached.timestamp > CACHE_DURATION) {
    cache.delete(key)
    return null
  }

  console.log('[CACHE HIT]', key)
  return cached.data
}

/**
 * Store data in cache with timestamp
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 */
export const setCache = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  })
  console.log('[CACHE SET]', key)
}

/**
 * Invalidate cache entries by key prefix
 * @param {string} prefix - Key prefix to match
 */
export const invalidateCache = (prefix) => {
  let count = 0
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key)
      count++
    }
  }
  if (count > 0) {
    console.log('[CACHE INVALIDATE]', prefix, `(${count} entries)`)
  }
}

/**
 * Clear all cache entries
 */
export const clearCache = () => {
  const size = cache.size
  cache.clear()
  console.log('[CACHE CLEAR]', `(${size} entries)`)
}

/**
 * Get cache statistics
 * @returns {object} Cache stats
 */
export const getCacheStats = () => {
  const now = Date.now()
  let expired = 0
  let valid = 0

  for (const [key, value] of cache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      expired++
    } else {
      valid++
    }
  }

  return {
    total: cache.size,
    valid,
    expired
  }
}

/**
 * Wrapper function to cache API calls
 * @param {string} key - Cache key
 * @param {Function} fetchFn - Async function to fetch data
 * @returns {Promise<any>} Cached or fresh data
 */
export const withCache = async (key, fetchFn) => {
  // Try to get from cache first
  const cached = getCached(key)
  if (cached !== null) {
    return cached
  }

  // Cache miss - fetch fresh data
  console.log('[CACHE MISS]', key)
  const data = await fetchFn()

  // Store in cache
  setCache(key, data)

  return data
}

export default {
  getCached,
  setCache,
  invalidateCache,
  clearCache,
  getCacheStats,
  withCache
}
